import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';
import { Button } from '@gelirgider/ui';
import { captureException } from '../lib/observability';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, {
      source: 'react-error-boundary',
      componentStack: info.componentStack,
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleHome = (): void => {
    window.location.assign('/');
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const t = i18n.getFixedT(i18n.language, 'admin');
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-lg rounded-lg border bg-card p-8 shadow-sm">
          <div className="mb-4 flex items-center gap-3 text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <h1 className="text-xl font-semibold">{t('error.boundary.title')}</h1>
          </div>
          <p className="mb-6 text-sm text-muted-foreground">{t('error.boundary.description')}</p>
          {import.meta.env.DEV ? (
            <details className="mb-6 rounded-md border bg-muted/40 p-3 text-xs">
              <summary className="cursor-pointer font-medium">{t('error.boundary.details')}</summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px]">
                {this.state.error.message}
                {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
              </pre>
            </details>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={this.handleReload} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              {t('error.boundary.reload')}
            </Button>
            <Button variant="outline" onClick={this.handleHome} className="gap-2">
              <Home className="h-4 w-4" />
              {t('error.boundary.home')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
